import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { APP_API_URL } from '../../utils';
import Swal from 'sweetalert2';
import DuoModal from '../../components/ui/duo-modal';

const isActiveValue = (value) => Number(value) === 1 || value === true;

export default function Update({ open, onClose, item, refreshData }) {
  const user = useSelector((state) => state.auth.user);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    manager: '',
    capacity: null,
    description: '',
    is_active: true,
    user_id: user?.id,
    code: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (!item) return;
    setFormData({
      name: item.name,
      location: item.location || '',
      manager: item.manager || '',
      capacity: item.capacity ?? null,
      description: item.description || '',
      is_active: isActiveValue(item.is_active),
      user_id: user?.id,
      code: item.code || '',
    });
    setErrors({});
  }, [item, user?.id]);

  if (!open || !item) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${APP_API_URL}/warehouses/${item.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: 'Le magasin a été mis à jour avec succès.',
        timer: 2000,
        showConfirmButton: false,
      });

      onClose();
      refreshData();
    } catch (error) {
      if (error.response?.status === 422) {
        setErrors(error.response.data.errors);
        Swal.fire({
          icon: 'error',
          title: 'Erreur de validation',
          text: 'Veuillez vérifier les champs du formulaire.',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Une erreur est survenue. Veuillez réessayer.',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DuoModal
      title={`Modifier — ${item.name}`}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="duo-btn duo-btn--ghost" onClick={onClose} disabled={isLoading}>
            Annuler
          </button>
          <button
            type="submit"
            form={`update-warehouse-form-${item.id}`}
            className="duo-btn duo-btn--primary"
            disabled={isLoading}
          >
            {isLoading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </>
      }
    >
      <form id={`update-warehouse-form-${item.id}`} onSubmit={handleSubmit}>
        <div className="duo-form-group">
          <label htmlFor={`warehouse-name-${item.id}`} className="duo-form-label">
            Nom du magasin *
          </label>
          <input
            type="text"
            className="duo-input"
            id={`warehouse-name-${item.id}`}
            name="name"
            value={formData.name}
            onChange={handleChange}
          />
          {errors.name && <p className="text-danger small mt-1 mb-0">{errors.name[0]}</p>}
        </div>
        <div className="row g-2">
          <div className="col-md-6">
            <div className="duo-form-group">
              <label htmlFor={`warehouse-location-${item.id}`} className="duo-form-label">
                Emplacement
              </label>
              <input
                type="text"
                className="duo-input"
                id={`warehouse-location-${item.id}`}
                name="location"
                value={formData.location}
                onChange={handleChange}
              />
              {errors.location && <p className="text-danger small mt-1 mb-0">{errors.location[0]}</p>}
            </div>
          </div>
          <div className="col-md-6">
            <div className="duo-form-group">
              <label htmlFor={`warehouse-manager-${item.id}`} className="duo-form-label">
                Responsable
              </label>
              <input
                type="text"
                className="duo-input"
                id={`warehouse-manager-${item.id}`}
                name="manager"
                value={formData.manager}
                onChange={handleChange}
              />
              {errors.manager && <p className="text-danger small mt-1 mb-0">{errors.manager[0]}</p>}
            </div>
          </div>
        </div>
        <div className="row g-2">
          <div className="col-md-6">
            <div className="duo-form-group">
              <label htmlFor={`warehouse-capacity-${item.id}`} className="duo-form-label">
                Capacité
              </label>
              <input
                type="number"
                className="duo-input"
                id={`warehouse-capacity-${item.id}`}
                name="capacity"
                value={formData.capacity ?? ''}
                onChange={handleChange}
              />
              {errors.capacity && <p className="text-danger small mt-1 mb-0">{errors.capacity[0]}</p>}
            </div>
          </div>
          <div className="col-md-6">
            <div className="duo-form-group">
              <label htmlFor={`warehouse-code-${item.id}`} className="duo-form-label">
                Code
              </label>
              <input
                type="text"
                className="duo-input"
                id={`warehouse-code-${item.id}`}
                name="code"
                value={formData.code}
                onChange={handleChange}
              />
              {errors.code && <p className="text-danger small mt-1 mb-0">{errors.code[0]}</p>}
            </div>
          </div>
        </div>
        <div className="duo-form-group">
          <label htmlFor={`warehouse-description-${item.id}`} className="duo-form-label">
            Description
          </label>
          <textarea
            className="duo-input"
            id={`warehouse-description-${item.id}`}
            name="description"
            rows={3}
            value={formData.description}
            onChange={handleChange}
          />
          {errors.description && <p className="text-danger small mt-1 mb-0">{errors.description[0]}</p>}
        </div>
        <div className="duo-form-group mb-0">
          <label className="duo-form-label d-flex align-items-start gap-2">
            <input
              type="checkbox"
              className="form-check-input mt-1"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
            />
            <span>
              Magasin actif
              <span className="d-block fw-normal" style={{ fontSize: '0.78rem', color: '#777' }}>
                Décochez pour rendre le magasin inactif.
              </span>
            </span>
          </label>
        </div>
      </form>
    </DuoModal>
  );
}
